import React from "react";

import { formatDateTime } from "@performyard/py-components";

// components
import FilterChips from "../../../components/FilterChips";
import { Filter } from "../../../components/FilterChips/FilterChips";
import { TODO_STATUS_OPTIONS, TodosFilters } from "./TodosFilterPanel";

const formatChipDate = (date: Date): string => formatDateTime(date, { hasTime: false, dateSize: "medium" }) ?? "";

const describeDateRange = (start?: Date, end?: Date): string => {
	if (start && end) {
		return `${formatChipDate(start)} – ${formatChipDate(end)}`;
	}
	if (start) {
		return `on or after ${formatChipDate(start)}`;
	}
	return `on or before ${formatChipDate(end as Date)}`;
};

export const convertTodosFiltersToReadableList = (filters: Partial<TodosFilters>): Filter[] => {
	const readableList: Filter[] = [];

	if (filters.assignee) {
		readableList.push({
			id: "assignee",
			type: "single-employee",
			label: "Assignee",
			value: { id: filters.assignee.id, name: filters.assignee.name },
		});
	}

	if (filters.workflow) {
		readableList.push({
			id: "workflow",
			type: "string",
			label: "Workflow",
			value: filters.workflow.name,
		});
	}

	if (filters.actor) {
		readableList.push({
			id: "actor",
			type: "single-employee",
			label: "Actor",
			value: { id: filters.actor.id, name: filters.actor.name },
		});
	}

	if (filters.status) {
		readableList.push({
			id: "status",
			type: "string",
			label: "Status",
			value: TODO_STATUS_OPTIONS.find((option) => option.value === filters.status)?.label ?? filters.status,
		});
	}

	if (filters.dueDateStart || filters.dueDateEnd) {
		readableList.push({
			id: "dueDate",
			type: "string",
			label: "Due Date",
			value: describeDateRange(filters.dueDateStart, filters.dueDateEnd),
		});
	}

	if (filters.completedDateStart || filters.completedDateEnd) {
		readableList.push({
			id: "completedDate",
			type: "string",
			label: "Completed Date",
			value: describeDateRange(filters.completedDateStart, filters.completedDateEnd),
		});
	}

	return readableList;
};

const CHIP_ID_TO_FILTER_KEYS: Record<string, (keyof TodosFilters)[]> = {
	assignee: ["assignee"],
	workflow: ["workflow"],
	actor: ["actor"],
	status: ["status"],
	dueDate: ["dueDateStart", "dueDateEnd"],
	completedDate: ["completedDateStart", "completedDateEnd"],
};

type ReadableTodosFilterChipsProps = {
	filters: Partial<TodosFilters>;
	setFilters: (newFilters: Partial<TodosFilters>) => void;
};

const ReadableTodosFilterChips = ({ filters, setFilters }: ReadableTodosFilterChipsProps) => {
	const convertedFilters = convertTodosFiltersToReadableList(filters);

	if (convertedFilters.length === 0) {
		return null;
	}

	return (
		<div>
			<FilterChips
				className="flex flex-wrap items-center gap-x-2 gap-y-1 [&>[data-testid=mainLabel]]:!mr-0"
				filters={convertedFilters}
				label="Filters applied: "
				onDeleteFilter={(id) => {
					const currentFilters = { ...filters };
					for (const key of CHIP_ID_TO_FILTER_KEYS[id] ?? [id as keyof TodosFilters]) {
						delete currentFilters[key];
					}

					setFilters({ ...currentFilters });
				}}
			/>
		</div>
	);
};

export default ReadableTodosFilterChips;
